/**
 * Bibliothèque et vente de livres : référentiels, règles de calcul partagées
 * (frais de livraison, totaux) et contrats d'API propres au module.
 * Montants en entiers FCFA, dates en ISO 8601.
 */
import type { BookSummaryDto, ImageAsset } from './contracts';
import type { BookFormat, Langue, PaymentMethod } from './referentials';

/* ---------- Référentiels ---------- */

export const BOOK_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export type BookStatus = (typeof BOOK_STATUSES)[number];

/** Catégories du catalogue (slug stocké, libellé affiché). */
export const BOOK_CATEGORIES = [
  { slug: 'coran-tafsir', label: 'Coran & tafsir' },
  { slug: 'hadith', label: 'Hadith' },
  { slug: 'biographie', label: 'Biographie' },
  { slug: 'khassaides', label: 'Khassaïdes' },
  { slug: 'jeunesse', label: 'Jeunesse' },
  { slug: 'spiritualite', label: 'Spiritualité' },
  { slug: 'histoire', label: 'Histoire de la communauté' },
] as const;
export type BookCategory = (typeof BOOK_CATEGORIES)[number]['slug'];
export const BOOK_CATEGORY_SLUGS = BOOK_CATEGORIES.map((c) => c.slug) as BookCategory[];

export function bookCategoryLabel(slug: string): string {
  return BOOK_CATEGORIES.find((c) => c.slug === slug)?.label ?? slug;
}

export const BOOK_FORMAT_LABELS: Record<BookFormat, string> = {
  NUMERIQUE: 'Numérique',
  PHYSIQUE: 'Papier',
};

export const LANGUE_LABELS: Record<Langue, string> = { fr: 'Français', wo: 'Wolof', ar: 'Arabe' };

export const DELIVERY_MODES = ['LIVRAISON_DAKAR', 'POINT_RELAIS'] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export const DELIVERY_MODE_LABELS: Record<DeliveryMode, string> = {
  LIVRAISON_DAKAR: 'Livraison à domicile (Dakar)',
  POINT_RELAIS: 'Retrait en point relais',
};

/** Frais de livraison (FCFA) : livraison Dakar 1 500, point relais gratuit. */
export const DELIVERY_FEES: Record<DeliveryMode, number> = {
  LIVRAISON_DAKAR: 1500,
  POINT_RELAIS: 0,
};

export const ORDER_STATUSES = ['PENDING_PAYMENT', 'PAID', 'PREPARING', 'SHIPPED', 'READY_FOR_PICKUP', 'DELIVERED', 'CANCELLED'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_PAYMENT: 'En attente de paiement',
  PAID: 'Payée',
  PREPARING: 'En préparation',
  SHIPPED: 'En cours de livraison',
  READY_FOR_PICKUP: 'Prête au point relais',
  DELIVERED: 'Livrée',
  CANCELLED: 'Annulée',
};

export const FREE_RESOURCE_KINDS = ['KHASSAIDE', 'SERMON'] as const;
export type FreeResourceKind = (typeof FREE_RESOURCE_KINDS)[number];

/** Quantité maximale par ligne de commande (livres papier). */
export const MAX_LINE_QUANTITY = 10;
export const MAX_ORDER_LINES = 20;

/* ---------- Règles pures (partagées PWA / API) ---------- */

export interface PricedLine {
  format: BookFormat;
  unitPrice: number;
  quantity: number;
}

/** Frais de livraison : 0 si la commande ne contient que du numérique. */
export function deliveryFee(lines: Pick<PricedLine, 'format'>[], mode: DeliveryMode | undefined): number {
  const hasPhysical = lines.some((l) => l.format === 'PHYSIQUE');
  if (!hasPhysical || !mode) return 0;
  return DELIVERY_FEES[mode];
}

export function orderTotals(lines: PricedLine[], mode: DeliveryMode | undefined): { subtotal: number; deliveryFee: number; total: number } {
  let subtotal = 0;
  for (const l of lines) {
    if (!Number.isInteger(l.unitPrice) || l.unitPrice < 0) throw new Error('Prix unitaire invalide');
    if (!Number.isInteger(l.quantity) || l.quantity < 1) throw new Error('Quantité invalide');
    subtotal += l.unitPrice * l.quantity;
  }
  const fee = deliveryFee(lines, mode);
  return { subtotal, deliveryFee: fee, total: subtotal + fee };
}

/* ---------- Contenu numérique ---------- */

/** Format structuré des livres numériques (léger, rendu sans moteur PDF). */
export interface BookContent {
  chapters: { title: string; paragraphs: string[] }[];
}

/* ---------- Contrats d'API ---------- */

export interface BookCategoryDto {
  slug: string;
  label: string;
  count: number;
}

export interface BookDetailDto extends BookSummaryDto {
  description: string;
  pageCount?: number;
  /** Un contenu numérique lisible est disponible. */
  hasContent: boolean;
  publishedAt?: string;
}

export interface AdminBookDto extends BookDetailDto {
  status: BookStatus;
  contentSizeBytes?: number;
  chapterCount?: number;
  createdAt: string;
  updatedAt: string;
  /** Exemplaires vendus (commandes payées, tous formats) — liste du back-office. */
  sales?: number;
}

/** Indicateurs du back-office bibliothèque (données réelles uniquement). */
export interface LibraryStatsDto {
  titles: number;
  published: number;
  digital: number;
  /** Titres papier avec moins de `lowStockThreshold` exemplaires. */
  lowStock: number;
  lowStockThreshold: number;
  /** Commandes payées ou en préparation contenant du papier. */
  toPrepare: number;
  toPrepareDakar: number;
  /** Exemplaires vendus et chiffre d'affaires (FCFA) de l'année civile en cours. */
  year: number;
  soldUnits: number;
  revenue: number;
}

export interface BookListQuery {
  q?: string;
  category?: string;
  language?: Langue;
  format?: BookFormat;
  free?: boolean;
  page?: number;
  pageSize?: number;
}

export interface UpsertBookBody {
  title: string;
  slug?: string;
  author: string;
  description: string;
  category: BookCategory;
  language: Langue;
  formats: BookFormat[];
  priceDigital?: number;
  pricePhysical?: number;
  stock?: number;
  pageCount?: number;
  isFree: boolean;
  status: BookStatus;
}

export interface StockAdjustBody {
  /** Variation (positive : réception, négative : casse / inventaire). */
  delta: number;
  reason: string;
}

export interface FreeResourceDto {
  id: string;
  kind: FreeResourceKind;
  title: string;
  author?: string;
  language: Langue;
  description?: string;
  /** Texte (khassaïdes), présent uniquement dans le détail. */
  paragraphs?: string[];
  hasAudio: boolean;
  durationSeconds?: number;
  audioSizeBytes?: number;
  /** Chemin d'écoute en flux (HTTP Range) — jamais un lien de téléchargement brut. */
  audioUrl?: string;
  published: boolean;
  createdAt: string;
}

export interface UpsertFreeResourceBody {
  kind: FreeResourceKind;
  title: string;
  author?: string;
  language: Langue;
  description?: string;
  paragraphs?: string[];
  durationSeconds?: number;
  published: boolean;
}

export interface RelayPointDto {
  id: string;
  name: string;
  quartier: string;
  address: string;
  phone?: string;
  hours?: string;
  active: boolean;
}

export type UpsertRelayPointBody = Omit<RelayPointDto, 'id'>;

export interface CheckoutLine {
  bookId: string;
  format: BookFormat;
  quantity: number;
}

export interface CheckoutDelivery {
  mode: DeliveryMode;
  quartier?: string;
  address?: string;
  relayPointId?: string;
  phone: string;
  recipientName?: string;
}

export interface CheckoutBody {
  lines: CheckoutLine[];
  /** Obligatoire dès qu'un livre papier est commandé. */
  delivery?: CheckoutDelivery;
  method: PaymentMethod;
  payerPhone?: string;
  idempotencyKey: string;
}

export interface OrderLineDto {
  bookId: string;
  title: string;
  author: string;
  format: BookFormat;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  cover?: ImageAsset;
}

export interface OrderDto {
  id: string;
  reference: string;
  status: OrderStatus;
  lines: OrderLineDto[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  delivery?: CheckoutDelivery & { relayPointName?: string };
  stockIssue: boolean;
  receiptNumber?: string;
  history: { status: OrderStatus; at: string; note?: string }[];
  paidAt?: string;
  createdAt: string;
}

export interface AdminOrderDto extends OrderDto {
  customer: { id: string; name: string; phone: string };
}

export interface UpdateOrderStatusBody {
  status: OrderStatus;
  note?: string;
}

export interface MyBookDto {
  book: BookSummaryDto & { hasContent: boolean; pageCount?: number };
  source: 'PURCHASE' | 'FREE';
  orderReference?: string;
  grantedAt: string;
}

export interface BookKeyDto {
  /** Clé AES-256-GCM (base64), propre à l'acheteur × au livre. */
  key: string;
  /** Filigrane affiché en surimpression : « Prénom Nom · +221 77 *** ** 01 · ZL… ». */
  watermark: string;
  contentType: 'application/vnd.zakat.book+json';
}
